import { useEffect, useRef, useState } from 'react';

/**
 * useOrderStatusStream — Cluster 6 · P7.
 *
 * Opens a Server-Sent-Events connection to /api/orders/{id}/status-stream
 * and returns the latest snapshot the server has emitted. Falls back
 * silently if the browser doesn't support EventSource (very old IE
 * only) — the caller can still poll via getStatusDetails().
 *
 * Handles reconnect on 30-min server-side max-duration timeout by
 * bumping the ``epoch`` state, which the useEffect uses as a dep.
 */
export default function useOrderStatusStream(orderId) {
  const [status, setStatus] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [epoch, setEpoch] = useState(0);
  const esRef = useRef(null);

  useEffect(() => {
    if (!orderId) return undefined;
    if (typeof EventSource === 'undefined') {
      setError('Realtime updates not supported in this browser.');
      return undefined;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Not signed in.');
      return undefined;
    }
    const backend = process.env.REACT_APP_BACKEND_URL;
    const url = `${backend}/api/orders/${encodeURIComponent(orderId)}/status-stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener('open', () => { setConnected(true); setError(null); });
    es.addEventListener('snapshot', (e) => {
      try { setStatus(JSON.parse(e.data)); } catch { /* ignore malformed */ }
    });
    es.addEventListener('status', (e) => {
      try { setStatus(JSON.parse(e.data)); } catch { /* ignore malformed */ }
    });
    es.addEventListener('timeout', () => {
      // Server told us to reconnect (30-min cap). Bump epoch.
      es.close();
      setEpoch(prev => prev + 1);
    });
    es.addEventListener('gone', () => {
      es.close();
      setError('Order no longer exists.');
    });
    es.addEventListener('error', () => {
      // Native EventSource auto-reconnects with backoff; only surface
      // as an error if we never opened.
      if (!connected) {
        setError('Live connection failed. Falling back to polling.');
      }
    });

    return () => { es.close(); };
  }, [orderId, epoch]); // 'connected' intentionally omitted — closure snapshot is desired

  return { status, connected, error };
}
